import { css, jsx } from '@emotion/core';
import { Icon, notification, Skeleton } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';

import { generateHTMLString } from '@/src/pdf/templates/meeting';
import { convertHtmlToPdf } from '@/src/pdf/utils';
import AdminLink from '@/src/shared/components/AdminLink';
import AttachmentLink from '@/src/shared/components/AttachmentLink';
import Button from '@/src/shared/components/Button';
import buttonStyle from '@/src/shared/components/Button/style';
import ContactCard from '@/src/shared/components/ContactCard';
import ContactCTA from '@/src/shared/components/ContactCTA';
import ContactLinks from '@/src/shared/components/ContactLinks';
import Container from '@/src/shared/components/Container';
import Divider from '@/src/shared/components/Divider';
import Head from '@/src/shared/components/Head';
import Heading from '@/src/shared/components/Heading';
import Hero from '@/src/shared/components/Hero';
import Layout from '@/src/shared/components/Layout';
import Link from '@/src/shared/components/Link';
import Sanitize from '@/src/shared/components/Sanitize';
import Section from '@/src/shared/components/Section';
import Table from '@/src/shared/components/Table';
import WebLogImage from '@/src/shared/components/WebLogImage';
import { bp, colors, space } from '@/styles';
import request from '@/utils/axios';
import { useSelector } from 'react-redux';
import { validateExternalUrl } from '@/utils/linkHelper';

import contentStyle from '../../../contentStyle';
import useIcal from './useICal';
import { useCallback } from 'react';

const validateHtmlContent = htmlContent => {
  return (
    htmlContent &&
    htmlContent
      .replace(/<\/?[^>]*>/g, '')
      .replace(/&nbsp;/g, '')
      .trim()
  );
};

const Subsection = props => {
  const {
    headingText,
    children,
    css: _css = css`
      clear: both;
    `,
    ..._props
  } = props;
  
  return (
    <>
      <Heading type={3} weight="bolder" css={[space.m.b16, _css]} theme="primaryGray" {..._props}>
        {headingText}
      </Heading>
      <div css={space.m.b32}>{children}</div>
    </>
  );
};

const SubsectionWrap = memo(props => {
  const { htmlContent } = props;
  const content = validateHtmlContent(htmlContent);
  if (!content) {
    return null;
  }
  return (
    <Subsection {...props}>
      <Sanitize>{htmlContent}</Sanitize>
    </Subsection>
  );
});

const LinkButton = memo(props => {
  const { iconType, url, linkText, type, hide, id } = props;
  if (!hide && validateExternalUrl(url)) {
    const buttonType = buttonStyle.type[type || 'default'];
    return (
      <a href={url} id={id} css={[buttonStyle.size.small, buttonType, space.m.r16]}>
        <Icon type={iconType} css={space.m.r4} /> {linkText}
      </a>
    );
  }
  return null;
});

const style = {
  body: [
    contentStyle,
    css`
      padding: 52px 32px;
      background-color: white;
      border-radius: 4px;
      box-shadow: 0 6px 12px 0 rgba(35, 32, 32, 0.1);
      @media print {
        box-shadow: none;
        padding: 16px 0;
      }
    `,
  ],
  buttonGroup: css`
    margin: 0 0 16px;
  `,
  legacyBody: css`
    margin-bottom: 32px;

    .PublicationHeading1,
    a[name='relatedinformation'],
    .PublicationDate {
      font-size: 2rem;
      line-height: 3.2rem;
      margin-bottom: 16px;
      font-weight: bold;
      color: ${colors.secondaryGrayDark};
    }

    .PublicationTitle {
      font-size: 2.8rem;
      line-height: 3.6rem;
      margin-bottom: 16px;
      font-weight: bold;
      color: ${colors.secondaryGrayDark};
    }

    .PublicationTopicHeading {
      font-weight: bold;
      color: ${colors.secondaryGrayDark};
    }
  `,
  contactLabel: css`
    margin-right: 6px;
    display: inline-block;
  `,
  title: {
    container: css`
      display: flex;
      align-items: center;
    `,
    text: css`
      display: inline-block;
    `,
    icon: css`
      color: ${colors.secondaryBlue};
      font-size: 5.6rem;
      margin-right: 32px;
    `,
  },
  heroinner: {
    container: css`
      width: 100%;
      display: block;
      margin-bottom: -16px !important;
      @media ${bp.md} {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
      }
    `,
    lhs: css`
      flex: 1;
      margin-bottom: 16px;
      @media ${bp.sm} {
        margin-right: 32px;
      }
      @media print {
        margin-right: 0;
      }
    `,
    rhs: css`
      flex: 0 0 auto;
      margin-bottom: 16px;
      .ant-btn-group {
        @media print {
          display: none;
        }
      }
    `,
    bottom: css`
      margin: 3.6rem 0 0;
    `,
  },
  attachmentContainer: css`
    .ant-table-body {
      @media print {
        overflow-x: hidden !important;
      }
    }
  `,
  legalContainer: css`
    display: flex;
  `,
  legalSeparator: css`
    margin: 0 4px;
  `,
  location: css`
    margin-bottom: 24px;
    a {
      color: ${colors.primaryBlue};
      &:hover {
        color: ${colors.primaryRed};
      }
    }
    p {
      margin-bottom: 0px;
    }
  `,
  heroButtons: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  `,
  addMeButtonContainer: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  addMeButton: css`
    font-size: 1.4rem;
    line-height: 2rem;
    padding: 6px 21px;
    letter-spacing: initial;
    text-transform: none;
  `,
  addMeButtonHelpText: css`
    font-size: 1.2rem;
    line-height: 1.618;
    color: ${colors.secondaryGray};
  `
};

function getMeetingDates(start, end) {
  const eventStart = moment(start);
  const eventEnd = moment(end);
  const amountOfDaysBetween = eventEnd.diff(eventStart, 'days');
  const isSingleDayEvent = amountOfDaysBetween === 0;
  if (isSingleDayEvent) {
    return `${eventStart.format('LL')} from ${eventStart.format('LT')} to ${eventEnd.format('LT')}`;
  }
  return `${eventStart.format('LLL')} to ${eventEnd.format('LLL')}`;
}

const CMSPage = ({ data, path, loading }) => {
  const [fileAttachments, setFileAttachments] = useState([]);
  const [pdfExportStatus, setPdfExportStatus] = useState(false);
  const [isAddMeLoading, setIsAddMeLoading] = useState(false);
  const [isAddMeSuccess, setIsAddMeSuccess] = useState(false);

  const { SitePageId, IcsCreated, Title, ExecutiveSummary, WebPosterId, OtherEditors } = data;
  const iCalFile = useIcal(IcsCreated === 'Y', SitePageId);
  const PersonID = useSelector(state => state.auth.userinfo?.PersonID);

  const attachmentsExist = Array.isArray(data.Attachments) && data.Attachments.length > 0;
  const passed2YearMark = data.PublishDateTime && moment().diff(data.PublishDateTime, 'years') >= 2;

  useEffect(() => {
    if (!SitePageId) return;
    if (attachmentsExist) {
      const handleAttachments = async () => {
        const filesList = [];
        for (let i = 0; i < data.Attachments.length; i++) {
          const url = `/attachment/page/${data.Attachments[i].AttachmentId}`;
          const newObj = {
            ...data.Attachments[i],
            href: url,
          };
          filesList.push(newObj);
        }
        setFileAttachments(filesList);
      };
      handleAttachments();
    }
  }, [SitePageId]);

  const title = loading ? (
    <Skeleton title={{ width: '50%' }} paragraph={false} />
  ) : (
    <span css={style.title.container}>
      <span css={style.title.text}>{Title}</span>
    </span>
  );

  const contacts = [];
  if (!passed2YearMark) {
    if (data.MeetingContact) {
      contacts.push({
        fullname: data.MeetingContact,
        phone: data.MeetingContactPhone,
        email: data.MeetingContactEmail,
        title: data.MeetingContactTitle,
        id: data.MeetingContactId,
        displayPicture: data.MeetingContactDisplayPicture === 'Y',
        initials: data.MeetingContactInitials,
        companyid: data.MeetingContactCompanyId,
        company: data.MeetingContactCompany,
      });
    }

    if (data.AlternateContact) {
      contacts.push({
        fullname: data.AlternateContact,
        email: data.AlternateContactEmail,
        title: data.AlternateContactTitle,
        id: data.AlternateContactId,
        phone: data.AlternateContactPhone,
        displayPicture: data.AlternateContactDisplayPicture === 'Y',
        initials: data.AlternateContactInitials,
        companyid: data.AlternateContactCompanyId,
        company: data.AlternateContactCompany,
      });
    }
  }

  const location = (() => {
    const entered = (data.MeetingVenueEntered || '').trim();
    if (data.MeetingTypeId !== 4) return entered || data.MeetingVenue;
    return entered || 'Webinar';
  })();

  const isLegacy = new Date(data.PublishDateTime) < new Date('03/01/2020 12:00:00 AM');

  const handleExportToPdf = async () => {
    setPdfExportStatus(true);
    const fileName = `Meeting-${Title}.pdf`;
    const html = generateHTMLString({ data, contacts });
    await convertHtmlToPdf({ html, fileName }, setPdfExportStatus);
  };

  function renderBody() {
    const attachments = attachmentsExist && (
      <Subsection
        headingText={isLegacy ? 'Related Information' : 'Attachments'}
        {...(isLegacy
          ? {
              id: 'relatedinformation',
              css: space.m.t32,
            }
          : {
              id: 'relatedinformation',
            })}
      >
        <Table
          css={style.attachmentContainer}
          showHeader={false}
          dataSource={fileAttachments}
          rowKey="AttachmentId"
          pagination={false}
          columns={[
            {
              dataIndex: 'AttachmentId',
              render: (id, attch) => {
                return (
                  <AttachmentLink attch={attch} />
                );
              },
            },
          ]}
        />
      </Subsection>
    );

    if (isLegacy)
      return (
        <article css={style.legacyBody}>
          <Sanitize>
            {(data.Body || '')
              .replace(/<br><br>/g, "<div class='lineBreak'></div>")
              .replace(/<i>\s<\/i>/g, '')
              .replace(/>\s</g, '><')
              .replace(/<p [^>]*>(?:(?:<br>)|\s)<\/p>/g, '')
              .replace(/^\[|\]$/gm, '')}
          </Sanitize>
          {attachments}
        </article>
      );
    return (
      <>
        <SubsectionWrap headingText="Who Should Attend" htmlContent={data.MeetingAttend} />
        <SubsectionWrap headingText="Additional Information" htmlContent={data.Body} />
        {data.MeetingRegistrationUrl && data.MeetingRegistrationUrl.trim() ? (
          <Subsection headingText="Registration">
            <p>
              This online form is a fast and easy way to register. Be sure to{' '}
              <Link href={data.MeetingRegistrationUrl} id="ga-register-text" anchor target="_blank">
                register today
              </Link>
              . Once you have completed the online registration, you will receive a confirmation
              notice within 24 hours.
            </p>
          </Subsection>
        ) : null}
        {attachments}
      </>
    );
  }

  const handleAddSelf = useCallback(async () => {
    setIsAddMeLoading(true);
    try {      
      const response = await request({
        url: `cms/addusertomeeting`,
        method: 'PATCH',
        params: {
          sitePageId: SitePageId,
          personId: PersonID,
        },
        triggerLoad: false,
        cache: false,
      });
      const { data } = response;
  
      if (data?.Success === 'true') {
        notification.success({
          message: 'Success',
          description: data.Message,
        });
        setIsAddMeSuccess(true);
      } 
  
      if (data?.Success === 'false') {
        notification.error({
          message: 'Error',
          description: data?.Errors?.ErrorList?.[0]?.ErrorMessage ?? data.Message,
        });
      }
    } catch (error) {
      console.error(error);
      notification.error({
        message: 'Error',
        description: 'Failed to send email invitation',
      });
    }
    setIsAddMeLoading(false);
  }, [SitePageId, PersonID])

  const isCalendarMeeting = data.SendFullText === 'C';
  const isCancelled = data.CancelIcs === 'Y';
  const isFutureEvent = data.DateMeetingStart ? new Date(data.DateMeetingStart).getTime() >= new Date().getTime() : false;
  const isAlreadyInvited = (data.Customers ?? []).map((c) => c?.PersonId).includes(PersonID) || isAddMeSuccess;
  const isMeetingOwner = data.SendFromId === PersonID;
  const showAddMe = isCalendarMeeting && Boolean(PersonID) && !isAlreadyInvited && !isMeetingOwner && isFutureEvent && !isCancelled;
  const showAddToICal = data.IcsCreated === 'Y' && Boolean(iCalFile.href);

  return (
    <Layout>
      {loading ? null : <Head title={Title} description={ExecutiveSummary} />}
      <AdminLink
        pageID={SitePageId}
        authorId={WebPosterId}
        editorIds={OtherEditors?.length ? OtherEditors.map(e => e.PersonId) : []}
        pageType="meeting"
      />
      <Hero
        title={title}
        overrideLast={{
          path,
          name: loading ? '' : Title,
        }}
      >
        <div css={style.heroinner.container}>
          {!isLegacy && !loading ? (
            <>
              {data.MeetingType !== 'Meeting Minutes' && (
                <section css={style.heroinner.lhs}>
                  <div>
                    <Heading type={5} weight="bold" theme="primaryGray" css={space.m.b8}>
                      Meeting Dates
                    </Heading>
                    <p css={space.m.b24}>
                      {data.MeetingDateDisplay ||
                        getMeetingDates(data.DateMeetingStart, data.DateMeetingEnd)}
                    </p>
                    <Heading type={5} weight="bold" theme="primaryGray" css={space.m.b8}>
                      Location
                    </Heading>
                    <Sanitize css={style.location}>{location}</Sanitize>
                    <Heading type={5} weight="bold" theme="primaryGray" css={space.m.b8}>
                      Meeting Fees
                    </Heading>
                    <p css={space.m.b24}>{data.MeetingFees || 'None'}</p>
                  </div>
                  <LinkButton
                    url={data.MeetingRegistrationUrl}
                    linkText="Register Now"
                    iconType="link"
                    id="ga-register-btn"
                  />
                  <LinkButton
                    url={data.AdditionalInfoUrl}
                    linkText="Additional Information"
                    hide={data.MeetingType !== 'Meeting' || !validateHtmlContent(data.Body)}
                    iconType="link"
                    type="gray"
                  />
                  <div css={style.heroButtons}>
                    {/*(showAddMe || showAddToICal) && (
                      <Divider type="red" />
                    )*/}
                    {showAddMe && (
                      <div css={style.addMeButtonContainer}>
                        <Button 
                          css={style.addMeButton} 
                          theme="darkOutlined"
                          onClick={handleAddSelf}
                          disabled={isAddMeLoading}
                        >
                          Add yourself to this meeting!
                        </Button>
                        <span css={style.addMeButtonHelpText}>This will send you an email with a calendar invitation.</span>
                      </div>
                    )}
                    {showAddToICal && (
                      <AttachmentLink
                        attch={iCalFile}
                        theme="outlined"
                        iconType="schedule"
                        linkText="Add to iCal"
                        linkStyle={[buttonStyle.size.small, buttonStyle.type.gray]}
                        iconStyle={space.m.r4}
                        download
                      />
                    )}
                  </div>
                </section>
              )}
            </>
          ) : null}
        </div>
        <div css={[style.heroinner.container, style.heroinner.bottom]}>
          <section css={style.heroinner.lhs}>
            {passed2YearMark ? (
              <ContactCTA />
            ) : (
              <ContactLinks
                title={`Contact${contacts.length > 1 ? 's' : ''}:`}
                contacts={contacts}
              />
            )}
          </section>
          <section css={style.heroinner.rhs}>
            {isLegacy ? (
              <Button.Group size="small" theme="gray">
                {attachmentsExist ? (
                  <Button id="ga-related-info" href="#relatedinformation" htmlType="button">
                    <Icon type="info-circle" /> Related Information
                  </Button>
                ) : null}
                <Button onClickPrint id="ga-print">
                  <Icon type="printer" theme="filled" /> Print
                </Button>
                <Button
                  onClickExportPDF={handleExportToPdf}
                  disabled={pdfExportStatus || loading}
                  id="ga-pdf"
                >
                  <Icon type="file-pdf" theme="filled" /> Export PDF
                </Button>
              </Button.Group>
            ) : (
              <Button.Group size="small" theme="gray" css={style.buttonGroup}>
                {attachmentsExist ? (
                  <Button id="ga-related-info" href="#relatedinformation" htmlType="button">
                    <Icon type="info-circle" /> Related Information
                  </Button>
                ) : null}
                <Button onClickPrint id="ga-print">
                  <Icon type="printer" theme="filled" /> Print
                </Button>
                <Button
                  onClickExportPDF={handleExportToPdf}
                  disabled={pdfExportStatus || loading}
                  id="ga-pdf"
                >
                  <Icon type="file-pdf" theme="filled" /> Export PDF
                </Button>
              </Button.Group>
            )}
          </section>
        </div>
      </Hero>

      <Section bgColor="gray">
        <Container size="sm">
          <main css={style.body}>
            {loading ? (
              <Skeleton paragraph={{ rows: 12 }} active />
            ) : (
              <>
                {renderBody()}
                <div css={style.legalContainer}>
                  <Link href="/legal/copyright">Copyright Notice</Link>
                  <span css={style.legalSeparator}>&nbsp;|&nbsp;</span>
                  <Link href="/legal/disclaimer">Legal Disclaimer</Link>
                </div>
              </>
            )}
          </main>
        </Container>
      </Section>

      {contacts && contacts.length ? (
        <Section>
          <Container>
            <ContactCard title={`Contact${contacts.length > 1 ? 's' : ''}`} contacts={contacts} />
          </Container>
        </Section>
      ) : null}
      <WebLogImage browserPath title={Title} sitePageId={SitePageId} />
    </Layout>
  );
};

CMSPage.propTypes = {
  data: PropTypes.object.isRequired,
  path: PropTypes.string.isRequired,
};

export default CMSPage;
